import React, { memo, useCallback, useMemo, useState } from "react";
import { useAbba } from "@foundation/abba-client";
import MediaServicesSearchClient from "@foundation/media-services-search-client";
import { OutlinedSearchDropdown } from "@bloomberg/media-ui-dropdown";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import type { OutlinedSearchDropdownProps } from "@bloomberg/media-ui-dropdown/types";
import { classes } from "@bloomberg/media-ui-core";
import { debounce } from "@foundation/util";
import styles from "./quoteSearch.module.css";
import type { SearchQueryResult, SearchQueryTicker } from "~types/types";
import { getSessionIdCookie } from "~utils/sessionUtils";
import { useLocale } from "~hooks/i18n/useLocale";
import { useTranslateMessage } from "~utils/i18n";

type DropdownOptions = OutlinedSearchDropdownProps["options"];

export type QuoteSearchProps = {
  searchURL: string;
  className?: string;
  countryCode?: string;
};

const MemoizedOutlinedSearchDropdown = memo(OutlinedSearchDropdown);
const INPUT_DELAY = 300;

export default function QuoteSearch(props: QuoteSearchProps) {
  const { searchURL, className, countryCode } = props;
  const { abbaConfig } = useAbba();
  const { isJapanese } = useLocale();
  const translate = useTranslateMessage();

  const enableFuzziness =
    abbaConfig?.["quote-search"]?.enableFuzziness ?? false;

  const [suggestions, setSuggestions] = useState<DropdownOptions>([]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  const searchClient = useMemo(
    () =>
      new MediaServicesSearchClient({
        baseUrl: searchURL,
      }),
    [searchURL]
  );

  const getSuggestion = useCallback(
    ({ name, resource_id: resourceId, url }: SearchQueryTicker) => {
      let tickerUrl = url || `https://www.bloomberg.com/quote/${resourceId}`;

      if (isJapanese) {
        tickerUrl = tickerUrl.replace(
          "www.bloomberg.com",
          "www.bloomberg.com/jp"
        );
      }

      return {
        value: resourceId,
        subText: name,
        url: tickerUrl,
      };
    },
    [isJapanese]
  );

  const fetchSearchResults = useCallback(
    (searchQuery: string) => {
      if (!searchQuery) return undefined;

      return searchClient.lookup({
        query: searchQuery,
        includeBlocklisted: false,
        source: getSessionIdCookie(),
        size: 100,
        country: countryCode,
        types: ["Company", "Index", "Fund", "Currency"],
        boostParents: true,
        ...(enableFuzziness && {
          fuzziness: "auto",
          popularity: "watchlist",
        }),
      });
    },
    [searchClient, enableFuzziness, countryCode]
  );

  const debouncedFetchResults = debounce((searchQuery: string) => {
    if (!searchQuery) clearSuggestions();
    trackClickEvent(
      "ticker search",
      searchQuery,
      "user entry",
      "ticker search"
    );

    fetchSearchResults?.(searchQuery)?.then(
      ({ results: nextSuggestions }: SearchQueryResult) => {
        if (!nextSuggestions) {
          clearSuggestions();

          return;
        }

        // compute ticker suggestions based on locale
        setSuggestions(nextSuggestions.map(getSuggestion));
      }
    );
  }, INPUT_DELAY);

  const handleChange = useCallback(
    (option: DropdownOptions[0] | null) => {
      // track quote search
      trackClickEvent(
        "ticker search",
        `ticker-search-quote-search-module-${option?.value}`,
        "click",
        "ticker search"
      );

      window.location.href = option?.url || "#";
    },
    [trackClickEvent]
  );

  const translations = {
    searchForQuotes: translate({
      id: "searchForQuotes",
      defaultMessage: "Search for quotes",
    }),
    noResults: translate({
      id: "noResults",
      defaultMessage: "No Results",
    }),
  };

  return (
    <MemoizedOutlinedSearchDropdown
      className={classes([styles.autocomplete, className])}
      onTextChange={debouncedFetchResults}
      onChange={handleChange}
      id="autocomplete"
      inputProps={{ autocomplete: "off" }}
      variant="markets"
      placeholder={translations.searchForQuotes}
      noOptionsText={translations.noResults}
      options={suggestions}
      size="extraSmall"
    />
  );
}
